'use client';

import { ChevronUp } from 'lucide-react';
import Image from 'next/image';
import type { User } from 'next-auth';
import { signOut, useSession } from 'next-auth/react';
import { useTheme } from 'next-themes';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/ui/sidebar';
import { redirect, useRouter } from 'next/navigation';
import { toast } from './toast';
import { LoaderIcon } from './icons';
import { guestRegex } from '@/lib/constants';
import { isEn } from '@/lib/utils';
import { sslog } from '@/lib/utils/sslog';

export function SidebarUserNav({ user, onSettings }: { user: User, onSettings?:()=>void }) {
  const router = useRouter();
  const { data, status } = useSession();
  const { setTheme, theme } = useTheme();

  // const useType = data?.user?.type ?? 'unknown';

  // const isWechatUser = (data?.user?.type === 'native' && !data?.user?.image) || (data?.user?.type === 'wechat' && !data?.user?.image);

  const isGuest = guestRegex.test(data?.user?.email ?? '');

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            {status === 'loading' ? (
              <SidebarMenuButton className="data-[state=open]:bg-sidebar-accent bg-background data-[state=open]:text-sidebar-accent-foreground h-10 justify-between">
                <div className="flex flex-row gap-2">
                  <div className="size-6 bg-zinc-500/30 rounded-full animate-pulse" />
                  <span className="bg-zinc-500/30 text-transparent rounded-md animate-pulse">
                    {isEn ? 'Loading auth status' : '加载登录态'}
                  </span>
                </div>
                <div className="animate-spin text-zinc-500">
                  <LoaderIcon />
                </div>
              </SidebarMenuButton>
            ) : (
              <SidebarMenuButton
                data-testid="user-nav-button"
                className="data-[state=open]:bg-sidebar-accent bg-background data-[state=open]:text-sidebar-accent-foreground h-10"
                // className="h-10"
              >
                <Image
                  src={data?.user?.image ? data?.user?.image : `/images/sun.png`}
                  alt={user.email ?? 'User Avatar'}
                  width={24}
                  height={24}
                  className="rounded-full"
                />
                <span data-testid="user-email" className="truncate">
                  {isGuest ? 'Guest' : ((user?.email?.indexOf('@') ? user?.email?.split('@')[0] : user?.email) ?? data?.user?.name)}
                </span>
                <ChevronUp className="ml-auto" />
              </SidebarMenuButton>
            )}
          </DropdownMenuTrigger>
          <DropdownMenuContent
            data-testid="user-nav-menu"
            side="top"
            className="w-[--radix-popper-anchor-width]"
          >
            <DropdownMenuItem
              data-testid="user-nav-item-theme"
              className="cursor-pointer"
              onSelect={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
            >
              {`切换主题`}
            </DropdownMenuItem>


            <DropdownMenuSeparator />
            {process.env.online != 'true' &&
            <DropdownMenuItem asChild data-testid="user-nav-item-auth">
              <button
                type="button"
                className="w-full cursor-pointer"
                onClick={() => {
                  if (status === 'loading') {
                    toast({
                      type: 'error',
                      description:
                        isEn ? 'Checking authentication status, please try again!' : '正在检测登录状态，请稍后重试！',
                    });
                    return;
                  }
                  if (isGuest) {
                    redirect('/login');
                  } else {
                    redirect('/dashboard/privacy');
                  }
                }}
              >
                {'政策协议'}
              </button>
            </DropdownMenuItem>}
            {process.env.online !== 'true' &&
            <DropdownMenuItem asChild data-testid="user-nav-item-auth">
              <button
                type="button"
                className="w-full cursor-pointer"
                onClick={() => {
                  if (status === 'loading') {
                    toast({
                      type: 'error',
                      description:
                        isEn ? 'Checking authentication status, please try again!' : '正在检测登录状态，请稍后重试！',
                    });
                    return;
                  }

                  if (isGuest) {
                    redirect('/login');
                  } else {
                    redirect('/profile/');
                  }
                }}
              >
                {'联系客服'}
              </button>
            </DropdownMenuItem>}
            {process.env.online !== 'true' &&
            <DropdownMenuItem asChild data-testid="user-nav-item-auth">
              <button
                type="button"
                className="w-full cursor-pointer"
                onClick={() => {
                  onSettings && onSettings();



                  // if (status === 'loading') {
                  //   toast({
                  //     type: 'error',
                  //     description:
                  //       isEn ? 'Checking authentication status, please try again!' : '正在检测登录状态，请稍后重试！',
                  //   });

                  //   return;
                  // }

                  // if (isGuest) {
                  //   redirect('/login');
                  // } else {
                  //   redirect('/dashboard/settings/');
                  // }
                }}
              >
                {'设置'}
              </button>
            </DropdownMenuItem>}
            
            <DropdownMenuSeparator />
            {/* <DropdownMenuItem asChild data-testid="user-nav-item-auth">
              <button
                type="button"
                className="w-full cursor-pointer"
                onClick={() => {
                  if (status === 'loading') {
                    toast({
                      type: 'error',
                      description:
                        'Checking authentication status, please try again!',
                    });

                    return;
                  }

                  if (isGuest) {
                    signOut({
                      redirectTo: '/login',
                    });
                  } else {
                    signOut({
                      redirectTo: '/login',
                    });
                  }
                }}
              >
                {'退出账号'}
              </button>
            </DropdownMenuItem> */}
            <DropdownMenuItem asChild data-testid="user-nav-item-auth">
              <button
                type="button"
                className="w-full cursor-pointer"
                onClick={() => {
                  if (status === 'loading') {
                    toast({
                      type: 'error',
                      description:
                        isEn ? 'Checking authentication status, please try again!' : '正在检测登录状态，请稍后重试！',
                    });

                    return;
                  }

                  if (isGuest) {
                    router.push('/login');
                  } else {
                    signOut({
                      redirectTo: '/',
                    });
                  }
                }}
              >
                {isGuest ? '去登录' : '退出'}
              </button>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
